export type SelectedNoteScopeStatus = "inactive" | "active" | "broadened" | "stale" | "conflict";
export type SelectedNoteScopeMode = "selected_notes" | "all_current_browse_results";

export interface SelectedNoteScopeSourceFilters {
    noteTypes?: string[];
    formNames?: string[];
    serviceCodes?: string[];
    servicePrograms?: string[];
    serviceDescriptions?: string[];
    providers?: string[];
    caseManagers?: string[];
    assessmentStaff?: string[];
    dateRanges?: string[];
    severityIndicators?: string[];
    sortOrder?: string;
}

export interface SelectedNoteScope {
    status: SelectedNoteScopeStatus;
    mode: SelectedNoteScopeMode;
    noteIds: string[];
    patientIds: string[];
    source: "browse";
    sourceFilters: SelectedNoteScopeSourceFilters;
    selectedAt: string;
    updatedAt: string;
    label?: string;
    filteredNoteCount?: number;
    lastActiveNoteIds?: string[];
    conflictReason?: "patient_changed" | "note_unavailable" | "filter_intersection_empty" | "filter_changed";
}

export const defaultSelectedNoteScope: SelectedNoteScope = {
    status: "inactive",
    mode: "selected_notes",
    noteIds: [],
    patientIds: [],
    source: "browse",
    sourceFilters: {},
    selectedAt: "",
    updatedAt: ""
};

type ClinicalFilterShape = {
    noteTypes?: string[];
    formNames?: string[];
    serviceCodes?: string[];
    servicePrograms?: string[];
    serviceDescriptions?: string[];
    providers?: string[];
    caseManagers?: string[];
    assessmentStaff?: string[];
    dateRanges?: string[];
    severityIndicators?: string[];
    sortOrder?: string;
};

export function selectedNoteScopeSourceFilters(filters: ClinicalFilterShape): SelectedNoteScopeSourceFilters {
    return {
        noteTypes: filters.noteTypes ?? [],
        formNames: filters.formNames ?? [],
        serviceCodes: filters.serviceCodes ?? [],
        servicePrograms: filters.servicePrograms ?? [],
        serviceDescriptions: filters.serviceDescriptions ?? [],
        providers: filters.providers ?? [],
        caseManagers: filters.caseManagers ?? [],
        assessmentStaff: filters.assessmentStaff ?? [],
        dateRanges: filters.dateRanges ?? [],
        severityIndicators: filters.severityIndicators ?? [],
        sortOrder: filters.sortOrder ?? "newest"
    };
}

export function selectedNoteScopeSignature(scope: SelectedNoteScope): string {
    return JSON.stringify({
        status: scope.status,
        mode: scope.mode,
        noteIds: [...scope.noteIds].sort(),
        patientIds: [...scope.patientIds].sort(),
        sourceFilters: normalizeSourceFilters(scope.sourceFilters),
        filteredNoteCount: scope.filteredNoteCount,
        conflictReason: scope.conflictReason
    });
}

export function patientBoundaryMatches(scope: SelectedNoteScope, currentPatientIds: string[]): boolean {
    const current = new Set(currentPatientIds);
    return scope.patientIds.length === currentPatientIds.length && scope.patientIds.every(id => current.has(id));
}

export function sourceFiltersMatch(left: SelectedNoteScopeSourceFilters, right: SelectedNoteScopeSourceFilters): boolean {
    return JSON.stringify(normalizeSourceFilters(left)) === JSON.stringify(normalizeSourceFilters(right));
}

export function buildSelectedNotesScope(args: {
    noteIds: string[];
    patientIds: string[];
    sourceFilters: SelectedNoteScopeSourceFilters;
    now: string;
    selectedAt?: string;
    filteredNoteCount?: number;
}): SelectedNoteScope {
    return {
        status: "active",
        mode: "selected_notes",
        noteIds: [...args.noteIds],
        patientIds: [...args.patientIds],
        source: "browse",
        sourceFilters: args.sourceFilters,
        selectedAt: args.selectedAt || args.now,
        updatedAt: args.now,
        label: `${args.noteIds.length} selected ${args.noteIds.length === 1 ? "note" : "notes"}`,
        filteredNoteCount: args.filteredNoteCount
    };
}

export function buildAllCurrentBrowseResultsScope(args: {
    filteredNoteCount: number;
    noteIds?: string[];
    patientIds: string[];
    sourceFilters: SelectedNoteScopeSourceFilters;
    now: string;
}): SelectedNoteScope {
    return {
        status: "active",
        mode: "all_current_browse_results",
        noteIds: [...(args.noteIds ?? [])],
        patientIds: [...args.patientIds],
        source: "browse",
        sourceFilters: args.sourceFilters,
        selectedAt: args.now,
        updatedAt: args.now,
        label: `${args.filteredNoteCount} filtered ${args.filteredNoteCount === 1 ? "note" : "notes"}`,
        filteredNoteCount: args.filteredNoteCount
    };
}

export function broadenSelectedNoteScope(scope: SelectedNoteScope, now: string): SelectedNoteScope {
    return {
        ...scope,
        status: "broadened",
        lastActiveNoteIds: scope.mode === "selected_notes" ? scope.noteIds : scope.lastActiveNoteIds,
        updatedAt: now
    };
}

export function clearSelectedNoteScope(now: string): SelectedNoteScope {
    return {
        ...defaultSelectedNoteScope,
        updatedAt: now
    };
}

export function restoreSelectedNoteScope(args: {
    scope: SelectedNoteScope;
    patientIds: string[];
    sourceFilters: SelectedNoteScopeSourceFilters;
    now: string;
}): SelectedNoteScope | null {
    if (!args.scope.lastActiveNoteIds?.length || args.patientIds.length !== 1) return null;
    return buildSelectedNotesScope({
        noteIds: args.scope.lastActiveNoteIds,
        patientIds: args.patientIds,
        sourceFilters: args.sourceFilters,
        now: args.now,
        selectedAt: args.scope.selectedAt || args.now,
        filteredNoteCount: args.scope.filteredNoteCount
    });
}

export function revalidateSelectedNoteScope(
    scope: SelectedNoteScope,
    currentPatientIds: string[],
    currentSourceFilters: SelectedNoteScopeSourceFilters,
    now: string
): SelectedNoteScope {
    if (scope.status !== "active" && scope.status !== "broadened") return scope;
    if (currentPatientIds.length === 0 || scope.patientIds.length === 0) return scope;
    if (!patientBoundaryMatches(scope, currentPatientIds)) {
        return { ...scope, status: "conflict", conflictReason: "patient_changed", updatedAt: now };
    }
    if (scope.status === "active" && !sourceFiltersMatch(scope.sourceFilters, currentSourceFilters)) {
        return { ...scope, status: "stale", conflictReason: "filter_changed", updatedAt: now };
    }
    return scope;
}

export function linkedToSelectedNotes(supportingAssessmentIds: string[] | undefined, selectedNoteIds: Set<string>): boolean {
    return Boolean(supportingAssessmentIds?.some(noteId => selectedNoteIds.has(noteId)));
}

function normalizeSourceFilters(filters: SelectedNoteScopeSourceFilters): Required<SelectedNoteScopeSourceFilters> {
    return {
        noteTypes: sorted(filters.noteTypes),
        formNames: sorted(filters.formNames),
        serviceCodes: sorted(filters.serviceCodes),
        servicePrograms: sorted(filters.servicePrograms),
        serviceDescriptions: sorted(filters.serviceDescriptions),
        providers: sorted(filters.providers),
        caseManagers: sorted(filters.caseManagers),
        assessmentStaff: sorted(filters.assessmentStaff),
        dateRanges: sorted(filters.dateRanges),
        severityIndicators: sorted(filters.severityIndicators),
        sortOrder: filters.sortOrder ?? "newest"
    };
}

function sorted(values: string[] | undefined): string[] {
    return [...(values ?? [])].sort();
}
